import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import logo from "../../images/logo.png";

const Login = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({ email: "", password: "" });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.email.trim()) newErrors.email = "Email is required";
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
            newErrors.email = "Please enter a valid email address";

        if (!formData.password) newErrors.password = "Password is required";
        else if (formData.password.length < 6)
            newErrors.password = "Password must be at least 6 characters";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setIsSubmitting(true); // Show fullscreen loader

        try {
            const res = await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/login`,
                {
                    email: formData.email,
                    password: formData.password,
                }
            );

            // Expecting backend response: { token: "...", user: {...} }
            localStorage.setItem("token", res.data.token);

            toast.success("Logged in successfully");
            setIsSubmitting(false); // Hide loader
            navigate("/dashboard"); // redirect after login
        } catch (err) {
            const apiMsg =
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                "Login failed. Please try again.";
            const fieldErrors = err?.response?.data?.errors;

            setErrors((prev) => ({
                ...prev,
                api: apiMsg,
                ...(fieldErrors || {}),
            }));

            toast.error(apiMsg);
            setIsSubmitting(false); // Hide loader even if error
        }
    };

    return (
        <>
            {/* Fullscreen loader */}
            {isSubmitting && (
                <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center">
                    <div className="flex flex-col items-center">
                        <div className="h-12 w-12 border-4 border-white border-t-transparent rounded-full animate-spin mb-3" />
                        <p className="text-white text-lg font-medium">Logging in...</p>
                    </div>
                </div>
            )}

            <section className="bg-[#F5F7FB] min-h-screen flex items-center justify-center font-poppins py-10">
                <div className="bg-white border border-[#E5E7EB] rounded-2xl shadow-sm w-full max-w-xl px-6 sm:px-8 py-8 sm:py-10">
                    <div className="flex justify-center mb-6">
                        <Link to="/">
                            <img src={logo} alt="Slimming Doctor" className="h-12 sm:h-14 object-contain" />
                        </Link>
                    </div>

                    <h2 className="text-2xl sm:text-3xl font-bold text-[#2A3342] text-center mb-2">
                        Login
                    </h2>
                    <p className="text-center text-gray-600 mb-8">
                        Welcome back! Please login to your account.
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        {errors.api && (
                            <p className="text-center text-red-500 text-sm">{errors.api}</p>
                        )}

                        {/* Email */}
                        <div>
                            <label className="block text-[16px] font-medium text-black mb-1">
                                Email Address <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="email"
                                name="email"
                                placeholder="Enter your email address"
                                value={formData.email}
                                onChange={handleChange}
                                className={`w-full border rounded-md px-3 py-2.5 text-sm placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#35C3D9] focus:border-[#35C3D9] ${errors.email ? "border-red-500" : "border-gray-200"
                                    }`}
                            />
                            {errors.email && (
                                <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                            )}
                        </div>

                        {/* Password */}
                        <div>
                            <label className="block text-[16px] font-medium text-black mb-1">
                                Password <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="password"
                                name="password"
                                placeholder="Enter your password"
                                value={formData.password}
                                onChange={handleChange}
                                className={`w-full border rounded-md px-3 py-2.5 text-sm placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#35C3D9] focus:border-[#35C3D9] ${errors.password ? "border-red-500" : "border-gray-200"
                                    }`}
                            />
                            {errors.password && (
                                <p className="text-red-500 text-xs mt-1">{errors.password}</p>
                            )}
                        </div>

                        {/* Submit */}
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className={`w-full bg-[#56C7D5] hover:bg-[#37bfd9] text-white font-[500] px-10 py-3 rounded-lg transition ${isSubmitting ? "opacity-50 cursor-not-allowed" : ""
                                }`}
                        >
                            {isSubmitting ? "Logging in..." : "Login"}
                        </button>

                        {/* Sign Up Link */}
                        <p className="text-center text-sm text-gray-600">
                            Don't have an account?{" "}
                            <Link
                                to="/auth/signup"
                                className="text-[#56C7D5] hover:text-[#37bfd9] font-medium"
                            >
                                Sign Up
                            </Link>
                        </p>
                    </form>
                </div>
                <ToastContainer position="top-right" autoClose={2500} hideProgressBar />
            </section>
        </>
    );
};

export default Login;
